/* PLAYER */

import { NextEpisode, StreamType } from '@smart-tv/typings/content';

export enum TrickModes {
  RecordAllow = 0x08,
  StartoverAllow = 0x80,
  StartoverAllowPause = 0x40,
  StartoverAllowRewind = 0x20,
  StartoverAllowFastforward = 0x10,
  CatchupAllow = 0x08,
  CatchupAllowPause = 0x04,
  CatchupAllowRewind = 0x02,
  CatchupAllowFastforward = 0x01
}

export interface Stream {
  drmEngine: string;
  duration?: number;
  streamUrl: string;
  //startPoint is the position from which the stream should be resumed
  startPoint?: number;
  streamType: StreamType;
  trickmodes: number;
  licenseServerUrl?: string;
  licenseCertificateUrl?: string;
  fastForwardLimit: number;
  nextEpisode?: NextEpisode;
  title: string;
  subtitle: string;
  keepAliveIntervalInSecs: number;
  updateStartoverNextInSecs?: number;
}

declare global {
  interface Window {
    voPlayer: VoPlayer;
  }
}

export interface PlayerContructor {
  new (element: HTMLDivElement | null): Player;
}

interface VoPlayer {
  Player: PlayerContructor;
  events: PlayerEvent;
  voSubtitlesPlugin: unknown;
  voAnalyticsPlugin: unknown;
  voFCCStylePlugin: unknown;
}

export interface Player {
  play: () => void;
  mute: () => void;
  unmute: () => void;
  pause: () => void;
  close: () => void;
  init: (config: PlayerConfig) => void;
  open: (stream: VisualOnStream) => void;
  isPaused: () => boolean;
  isEnded: () => boolean;
  getVolume: () => number;
  addEventListener: <T>(event: string, callback: (payload: T) => void) => void;
  removeEventListener: <T>(event: string, callback: (payload: T) => void) => void;
  getQualityLevels: () => QualityLevel[];
  getCurrentQualityLevel: () => QualityLevel;
  selectQualityLevel: (id: string) => void;
  getSubtitleTracks: () => SubtitleTrack[];
  getDuration: () => number;
  getCurrentSubtitleTrack: () => SubtitleTrack;
  selectSubtitleTrack: (id?: string) => void;
  getAudioTracks: () => AudioTrack[];
  getCurrentAudioTrack: () => AudioTrack;
  selectAudioTrack: (id: string) => void;
  getPosition: () => number;
  getSeekableRange: () => { start: number; end: number };
  setPosition: (position: number) => void;
  setVolume: (volume: number) => void;
  addPlugin: (plugin: any) => void;
  setSubtitleStyles: (subtitleStyles: SubtitleStyles) => void;
  setBitrateThreshold: (bitrateThreshold: BitrateThreshold) => void;
}

/* EVENTS */

export enum PlayerEvent {
  PlayCompleted = 'VO_OSMP_CB_PLAY_COMPLETE',
  Opened = 'VO_OSMP_SRC_CB_OPEN_FINISHED',
  Paused = 'VO_OSMP_CB_PLAY_PAUSED',
  Playing = 'VO_OSMP_CB_PLAY_PLAYING',
  Closed = 'VO_OSMP_SRC_CB_CLOSED',
  PlayTimeUpdated = 'VO_OSMP_CB_PLAY_TIME_UPDATED',
  Waiting = 'VO_OSMP_CB_PLAY_WAITING',
  BitrateChanged = 'VO_OSMP_SRC_ADAPTIVE_STREAMING_INFO_EVENT_BITRATE_CHANGE',
  Error = 'VO_OSMP_CB_ERROR_EVENTS',
  LogAdded = 'VO_OSMP_CB_LOG_ADDED'
}
export enum PlayerErrorDecimalCodes {
  UnsupportedSourceErrorFormat = 2164260865,
  UnsuppotedSourceErrorCodec = 2164260866,
  BrowserUnsupportedMSE = 2164260867,
  ManifestDownloadFail = 2164260868,
  ManifestParseFail = 2164260869,
  ContentDownloadFail = 2164260870,
  ContentParseFail = 2164260871,
  TextTrackParseFail = 2164260872,
  SourceBufferError = 2164260873,
  OutMemory = 2164260874,
  Unknown = 2164260875,
  DRMBrowserUnsupported = 2164260876,
  DRMKeyFail = 2164260877,
  DRMKeySessionFail = 2164260878,
  DRMLicenseRequestFail = 2164260879,
  DRMSelectKeySystemFail = 2164260880,
  DRMDecryptFail = 2164260881,
  LicenseFail = 2164260882
}
 
/* CONFIG */

export interface PlayerConfig {
  analytics: [
    {
      type: string;
      options: { cuid: string; deviceId: string };
    }
  ];
  playback: { autoPlay: boolean };
  advanced: { startupBitrate: number };
  logsConfig?: boolean;
  logs?: {
    logLevel: number;
    logToConsole: boolean;
  };
}

/* STREAM */

export interface VisualOnStream {
  links: VisualOnStreamLink[];
  advanced: {
    anlyticsCustomData: {
      cd1: string;
      cd2: string;
    };
  };
}

export interface VisualOnStreamLink {
  uri: string;
  type: string;
  drm?: VisualOnStreamLinkDrm;
}

export interface VisualOnStreamLinkDrm {
  widevine?: {
    laUrl?: string;
  };
  fairplay?: {
    laUrl?: string;
    certificateUrl?: string;
  };
}

export interface StreamParams {
  sid?: number;
  mediaId?: number;
  eventId?: number;
  progress?: number;
  streamType?: StreamType;
}

/* SETTINGS */

export interface QualityLevel {
  bandwidth: number;
  codec: string;
  height: number;
  id: string;
  lang: string;
  mimeType: string;
  type: string;
  viewport?: string;
  width: number;
}

export interface SubtitleTrack {
  id: string;
  label: string;
  lang: string;
  kind: SubtitleTrackKind;
  roles?: SubtitleTrackRole[];
}

export enum SubtitleTrackKind {
  Subtitle = 'subtitles',
  Caption = 'captions'
}

export enum SubtitleTrackRole {
  Subtitle = 'subtitle',
  Caption = 'caption'
}

export interface SubtitleStyles {
  enable: boolean;
  previewSubtitleText: string;
  fontSize: {
    enable: boolean;
    value: string;
  };
}

export interface AudioTrack {
  codec: string;
  channels?: string;
  id: string;
  label: string;
  mimeType: string;
  lang: string;
  type: string;
  bandwidth: number;
  name: string;
  roles?: string[];
}

export interface BitrateChangedEvent {
  mediaType: 'video' | 'audio';
  oldQuality?: string | null;
  newQuality?: string;
  bandwidth?: number;
  width?: number;
  height?: number;
}

export interface BitrateThreshold {
  upper: number;
  lower: number;
}

export interface BitrateCap {
  id: number;
  title: string;
  value: number;
}

/* SEEKABLE RANGE */

export interface SeekableRange {
  duration: number;
  position: number;
  range: { start: number; end: number };
  live: boolean;
}

export interface Props {
  children?: React.ReactNode | React.ReactNode[];
  streamParams?: StreamParams;
  className?: string;
}
